import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { InlineAlert } from '@/components/notifications';
import { Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import logoMarkRed from '@/assets/branding/logo-mark-red.svg';
import {
  clearOidcLoginAttempt,
  clearOidcAutoLoginBlock,
  hasRecentOidcLoginAttempt,
  readOidcAutoLoginBlock,
} from '@/lib/authNavigationGuards';

const LOGOUT_GUARD_KEY = 'rugira_logout_guard';
const LOGOUT_GUARD_TTL_MS = 30000;

const clearLogoutGuard = () => {
  try {
    localStorage.removeItem(LOGOUT_GUARD_KEY);
    sessionStorage.removeItem(LOGOUT_GUARD_KEY);
  } catch {
    // ignore
  }
};

const resolveInitialNextPath = (): string => {
  if (typeof window === 'undefined') return '/overview';
  try {
    const params = new URLSearchParams(window.location.search);
    const next = params.get('next');
    if (!next) return '/overview';
    const decoded = decodeURIComponent(next);
    if (!decoded.startsWith('/')) return '/overview';
    return decoded;
  } catch {
    return '/overview';
  }
};

export default function Login() {
  const [, setLocation] = useLocation();
  const { performDemoLogin, isAuthenticated, authResolved, login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [nextPath] = useState(resolveInitialNextPath);
  const [oidcAutoLoginBlocked, setOidcAutoLoginBlocked] = useState(() =>
    readOidcAutoLoginBlock().active,
  );
  const [logoutGuardActive, setLogoutGuardActive] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const timestamp = localStorage.getItem(LOGOUT_GUARD_KEY);
      if (timestamp) {
        const guardAge = Date.now() - parseInt(timestamp, 10);
        if (guardAge < LOGOUT_GUARD_TTL_MS) {
          return true;
        }
        localStorage.removeItem(LOGOUT_GUARD_KEY);
      }
      if (sessionStorage.getItem(LOGOUT_GUARD_KEY) === '1') {
        return true;
      }
    } catch {
      return false;
    }
    return false;
  });
  const isKeycloakOnly = !performDemoLogin;

  useEffect(() => {
    const snapshot = readOidcAutoLoginBlock();
    setOidcAutoLoginBlocked(snapshot.active);
    if (!snapshot.active || typeof window === 'undefined') {
      return;
    }
    const timer = window.setTimeout(() => {
      setOidcAutoLoginBlocked(readOidcAutoLoginBlock().active);
    }, snapshot.remainingMs + 50);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  // If already authenticated, redirect immediately once auth status is settled
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (import.meta.env.DEV) {
      console.log('[Login] Auth state:', { authResolved, isAuthenticated, logoutGuardActive, performDemoLogin: !!performDemoLogin });
    }
    if (!authResolved || !isAuthenticated) {
      return;
    }

    const pathname = window.location.pathname || '';
    const atLoginPath = pathname === '/' || pathname.startsWith('/login');
    const shouldRedirect = !performDemoLogin || !atLoginPath;

    if (import.meta.env.DEV) {
      console.log('[Login] Redirect check:', { shouldRedirect, nextPath, pathname, atLoginPath });
    }
    if (shouldRedirect && nextPath && pathname !== nextPath) {
      if (import.meta.env.DEV) {
        console.log('[Login] Redirecting to:', nextPath);
      }
      setLocation(nextPath);
    }
  }, [authResolved, isAuthenticated, nextPath, setLocation, performDemoLogin, logoutGuardActive]);

  useEffect(() => {
    if (!isKeycloakOnly) return;
    if (logoutGuardActive || oidcAutoLoginBlocked) {
      setIsLoading(false);
      return;
    }
    if (hasRecentOidcLoginAttempt()) {
      // Prevent duplicate signinRedirect bursts while an OIDC redirect is already in-flight.
      return;
    }
    let cancelled = false;
    const triggerRedirect = async () => {
      try {
        setIsLoading(true);
        await login(nextPath);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Unable to reach Rugira sign-in.');
          setIsLoading(false);
        }
      }
    };
    triggerRedirect();
    return () => {
      cancelled = true;
    };
  }, [isKeycloakOnly, logoutGuardActive, oidcAutoLoginBlocked, login, nextPath]);

  const retryKeycloakLogin = async () => {
    clearLogoutGuard();
    clearOidcLoginAttempt();
    setLogoutGuardActive(false);
    clearOidcAutoLoginBlock();
    setOidcAutoLoginBlocked(false);
    setError('');
    try {
      setIsLoading(true);
      await login(nextPath);
    } catch (err: any) {
      setError(err?.message || 'Unable to reach Rugira sign-in.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    let redirecting = false;

    try {
      if (performDemoLogin) {
        const result = await performDemoLogin(username, password, nextPath);

        if (result === 'demo') {
          setTimeout(() => {
            setLocation(nextPath);
          }, 150);
          return;
        }

        if (result === 'oidc') {
          redirecting = true;
          return;
        }

        setError('Invalid username or password. Please try again.');
      } else {
        await login(nextPath);
        redirecting = true;
        return;
      }
    } catch (error: any) {
      setError(error.message || 'Authentication failed. Please try again.');
    } finally {
      if (!redirecting) {
        setIsLoading(false);
      }
    }
  };

  if (isKeycloakOnly) {
    const headingText = logoutGuardActive
      ? 'You are signed out'
      : oidcAutoLoginBlocked
        ? 'Sign-in paused'
        : 'Secure Sign-In';
    const descriptionText = logoutGuardActive
      ? 'Click below when you are ready to sign in again.'
      : oidcAutoLoginBlocked
        ? 'Previous sign-in callback failed. Retry when ready.'
        : 'Redirecting you to the Rugira identity provider…';

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-sm sm:max-w-md">
          <Card className="shadow-lg sm:shadow-2xl" data-testid="login-card">
            <CardHeader className="text-center space-y-4 sm:space-y-6 p-4 sm:p-6 lg:p-8">
              <div className="flex justify-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                  <img src={logoMarkRed} alt="Rugira" className="w-14 h-14 sm:w-16 sm:h-16" />
                </div>
              </div>
              <div>
                <CardTitle className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">
                  {headingText}
                </CardTitle>
                <CardDescription className="text-sm sm:text-base text-slate-600 dark:text-slate-300 mt-2">
                  {descriptionText}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="p-6 sm:p-8 pt-0 text-center">
              <div className="flex flex-col items-center space-y-4">
                {isLoading ? (
                  <Loader2
                    className="h-8 w-8 animate-spin text-brand-red"
                    data-testid="secure-signin-spinner"
                  />
                ) : null}
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  If nothing happens,{' '}
                  <button
                    onClick={retryKeycloakLogin}
                    className="text-brand-red font-semibold"
                  >
                    click here
                  </button>{' '}
                  to retry.
                </p>
                {error && (
                  <InlineAlert
                    tone="error"
                    icon={<AlertCircle className="h-4 w-4" />}
                    description={error}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-sm sm:max-w-md">
        <Card className="shadow-lg sm:shadow-2xl" data-testid="login-card">
          <CardHeader className="text-center space-y-4 sm:space-y-6 p-4 sm:p-6 lg:p-8">
            {/* Rugira Logo */}
            <div className="flex justify-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                <img src={logoMarkRed} alt="Rugira" className="w-14 h-14 sm:w-16 sm:h-16" />
              </div>
            </div>

            <div>
              <CardTitle className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">
                Welcome Back
              </CardTitle>
              <CardDescription className="text-sm sm:text-base text-slate-600 dark:text-slate-300 mt-2">
                Sign in to manage your trading operations
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="p-6 sm:p-8 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {error && (
                <InlineAlert
                  tone="error"
                  icon={<AlertCircle className="h-4 w-4" />}
                  description={error}
                />
              )}

              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm sm:text-base font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={isLoading}
                  data-testid="input-username"
                  className="h-11 sm:h-12 text-base border border-slate-300 dark:border-slate-600 focus:border-brand-red focus:ring-1 focus:ring-brand-red" // Larger touch targets with borders
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm sm:text-base font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    data-testid="input-password"
                    className="pr-12 h-11 sm:h-12 text-base border border-slate-300 dark:border-slate-600 focus:border-brand-red focus:ring-1 focus:ring-brand-red" // Larger touch targets with borders
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-[44px] min-h-[44px] justify-center" // 44px minimum touch target
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-slate-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-slate-400" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-brand-red hover:bg-brand-red/90 text-white h-11 sm:h-12 text-base font-semibold"
                disabled={isLoading}
                data-testid="button-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4 text-center p-6 sm:p-8 pt-4">
            <div className="flex items-center justify-center text-xs sm:text-sm text-slate-500">
              <a
                href="https://rugira.ch"
                className="hover:text-brand-red transition-colors duration-200 min-h-[44px] flex items-center"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit www.rugira.ch
              </a>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
